
import { Injectable } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { ErrorResponse } from '@shahi-analytics/backend-utils';
import { HourWiseDowntimeModel, HourWiseEfficiencyModel, HourWiseGarmentsModel, HourWiseReworkModel, LineDayHourAbstractInfoModel, LineDowntimeModel, LineDowntimeResponse, LineEfficiencyModel, LineOperationCompletionModel, LineOperationEfficiencyModel, LineOperationEfficiencyResponse, LineOperatorsModel, LineOperatorsReworkModel, LineOpportunityOperationsModel, LineOpportunityOperationsResponse, LineSmvModel, LineSmvResponse, LineWiseGarmentsModel, LineWorkStationEfficiencyModel, LineWorkStationReworkModel, LineWorkStationReworkResponse, LineWorkstationEfficiencyResponse, LineAbstractInfoModel, LinesInUnitModel, LinesInUnitResponse, LinesReworkModel, OperationCompletionModel, OperationCompletionResponse, OperationEfficiencyModel, OperationReworkModel, OperationSmvModel, OperationTimeConsModel, OperatorReworkModel, OperatorReworkResponse, OpportunityOperationWorkstationModel, OpportunityOperationsModel, PlannedActualRejGarmentsModel, PlannedActualRejGarmentsResponse, ReasonWiseDowntimeModel, StyleOperationsTimeConsModel, StyleOperationsTimeConsResponse, StyleSmvModel, UnitsAbstractInfoResponse, WorkstationDowntimeModel, WorkstationEfficiencyModel, WorkstationLevelGarmentsCompletion, WorkstationReworkModel, UnitsAbstractInfoModel } from '@shahi-analytics/shared-models';
import { In, Repository } from 'typeorm';
import { LineEntity } from '../entities/line.entity';
import { OperationEntity } from '../entities/operation.entity';
import { OperatorEntity } from '../entities/operator.entity';
import { ReasonEntity } from '../entities/reason.entity';
import { StyleOperationsEntity } from '../entities/style-operations.entity';
import { StyleEntity } from '../entities/style.entity';
import { LineActualDayTrackEntity } from '../entities/transactions/line-actual-track.entity';
import { LineDownTimeEntity } from '../entities/transactions/line-down-time.entity';
import { LineEfficiencyDayTrackEntity } from '../entities/transactions/line-efficiency.entity';
import { LineDayPlanEntity } from '../entities/transactions/line-plan';
import { OperatorEfficiencyDayTrackEntity } from '../entities/transactions/operator-efficiency-track.entity';
import { WorkstationEntity } from '../entities/workstation.entity';
import { UnitLineDateRequest } from './request/unit-line-date.request';
import { MigrationHelperService } from '../migration/migration-helper.service';
import { UnitEntity } from '../entities/unit.entity';
import { InaService } from './ina.service';
import { InaHelperService } from './ina.helper.service';

@Injectable()
export class CentralInaInfoService {

  constructor(
    @InjectRepository(UnitEntity) private unitEnt: Repository<UnitEntity>,
    @InjectRepository(LineActualDayTrackEntity) private lineActualDayTrackEnt: Repository<LineActualDayTrackEntity>,
    @InjectRepository(StyleEntity) private styleEnt: Repository<StyleEntity>,
    @InjectRepository(LineEntity) private lineEnt: Repository<LineEntity>,
    @InjectRepository(LineDayPlanEntity) private linePlanEnt: Repository<LineDayPlanEntity>,
    @InjectRepository(StyleOperationsEntity) private styleOpEnt: Repository<StyleOperationsEntity>,
    @InjectRepository(OperationEntity) private operationEnt: Repository<OperationEntity>,
    @InjectRepository(OperatorEfficiencyDayTrackEntity) private operatorEffEnt: Repository<OperatorEfficiencyDayTrackEntity>,
    @InjectRepository(WorkstationEntity) private workEnt: Repository<WorkstationEntity>,
    @InjectRepository(LineEfficiencyDayTrackEntity) private lineEffEnt: Repository<LineEfficiencyDayTrackEntity>,
    @InjectRepository(OperatorEntity) private operatorEnt: Repository<OperatorEntity>,
    @InjectRepository(LineDownTimeEntity) private downTimeEntity: Repository<LineDownTimeEntity>,
    @InjectRepository(ReasonEntity) private reasonEntity: Repository<ReasonEntity>,
    private migHelperService: MigrationHelperService,
    private inaService: InaService,
    private inaHelperService: InaHelperService
  ) { }


  async getLineLevelAbstarctInfoForAllUnits(date: string): Promise<UnitsAbstractInfoResponse> {
    try {
        const units = await this.unitEnt.find({select: ['unitId'], where: { isActive: true }});
        const unitAbstaracts: UnitsAbstractInfoModel[] = [];
        for(const unit of units) {
          const unitAbstractInfo = await this.constructLineLevelAbstarctInfoForUnit(unit.unitId, date);
          unitAbstaracts.push(unitAbstractInfo);
        }
        return new UnitsAbstractInfoResponse(true, 0, 'Units abstract info retrieved', unitAbstaracts);
    } catch (error) {
        throw error;
    }
  }

  async getLineLevelAbstarctInfoForUnit(req: UnitLineDateRequest): Promise<UnitsAbstractInfoResponse> {
    try {
        const units = await this.unitEnt.find({select: ['unitId'], where: { isActive: true, unitId: req.unit }});
        const unitAbstaracts: UnitsAbstractInfoModel[] = [];
        for(const unit of units) {
          const unitAbstractInfo = await this.constructLineLevelAbstarctInfoForUnit(unit.unitId, req.date);
          unitAbstaracts.push(unitAbstractInfo);
        }
        return new UnitsAbstractInfoResponse(true, 0, 'Units abstract info retrieved', unitAbstaracts);
    } catch (error) {
        throw error;
    }
  }

  // constructs the lines abstract info for all lines in the unit
  async constructLineLevelAbstarctInfoForUnit(unitId: string, date: string): Promise<UnitsAbstractInfoModel> {
    const unitInfo = await this.unitEnt.findOne({ select: ['unitName'], where: { unitId: unitId, isActive: true } });
    const dayPlan = await this.inaHelperService.getPlantQuantitiesStyleWise(unitId, [], date);
    const linesAbractInfo = await this.constructLinesAbstarctInfo(null, unitId, date, dayPlan);
    const errorMessage = dayPlan.size == 0 ? 'Daily production plan is not updated' : '';
    return new UnitsAbstractInfoModel(unitId, unitInfo.unitName, linesAbractInfo, linesAbractInfo.length, errorMessage);
  }



  // constructs the individual line abstract info
  async constructLinesAbstarctInfo(lineIds: string[], unitId: string, date: string, planQtyMap: Map<string, Map<string, Map<number, number>>>): Promise<LineAbstractInfoModel[]> {
    const linePerHourPlan = new Map<string, Map<number, number>>();
    const linePerHourAbstarctInfo = new Map<string, Map<number, LineDayHourAbstractInfoModel>>();
    const lineDowntimeMap = new Map<string, number>();
    const lineWorkstationsMap = new Map<string, Set<string>>();

    planQtyMap.forEach((lines, lineId)=> {
      linePerHourPlan.set(lineId, new Map<number, number>());
      lineWorkstationsMap.set(lineId, new Set<string>());
      linePerHourAbstarctInfo.set(lineId, new Map<number, LineDayHourAbstractInfoModel>());
      lineDowntimeMap.set(lineId, 0);
      lines.forEach(style => {
        style.forEach((qty, hour) => {
          if(!linePerHourPlan.get(lineId).has(hour)) {
            linePerHourPlan.get(lineId).set(hour, 0);
            linePerHourAbstarctInfo.get(lineId).set(hour, new LineDayHourAbstractInfoModel(hour, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0));
          }
          const preVal = linePerHourPlan.get(lineId).get(hour);
          // linePerHourPlan.get(lineId).set(hour, preVal + qty);
          // dont sum the qty based on the styles. Because we are currently not handling the 
          linePerHourPlan.get(lineId).set(hour, qty);
        });
      });
    });

    const linesInfo = await this.lineEnt.find({ select: ['lineDesc', 'lineNumber', 'lineId'], where: { unitId: unitId }});
    const lineInfoMap = new Map<string, LineEntity>();
    linesInfo.forEach(r => lineInfoMap.set(r.lineId, r));

    type tempLineInfo = { totalOperators: Set<string>, totalStyles: Set<string>, totalRework: number, totalOutput: number, accountableWorkstations: Set<string>, totalWorkstations: Set<string>, productiveMinutes: number };
    const lineHourLevelQtysTempMap = new Map<string, Map<number, tempLineInfo>>();
    const hourWiseLineOutputQuery = this.lineActualDayTrackEnt.createQueryBuilder('lat')
    .select(' hour, line_id, good_quantity, rejected_quantity, operator_id, style_id, workstation_id, is_rework, operation_smv, is_last_op  ')
    .where(` plan_date = '${date}' AND unit_id = '${unitId}' `);
    if(lineIds) {
      hourWiseLineOutputQuery.andWhere(`line_id IN (:...lines)`, { lines: lineIds } );
    }
    const hourWiseLineOutputResponse: {hour: number, line_id: string, good_quantity: number, rejected_quantity: number, operator_id: string, style_id: string, workstation_id: string,is_rework: boolean, operation_smv: number, is_last_op: boolean}[] = await hourWiseLineOutputQuery.getRawMany();
    hourWiseLineOutputResponse.forEach(rec => {
      const currLineId = rec.line_id;
      if(!lineHourLevelQtysTempMap.has(currLineId)) {
        lineHourLevelQtysTempMap.set(currLineId, new Map<number, tempLineInfo>());
      }
      if(!lineHourLevelQtysTempMap.get(currLineId).has(rec.hour)) {
        lineHourLevelQtysTempMap.get(currLineId).set(rec.hour, { totalOperators: new Set<string>(), totalStyles: new Set<string>(), totalRework: 0, totalOutput: 0, accountableWorkstations: new Set<string>(), totalWorkstations: new Set<string>(), productiveMinutes: 0 })
      }
      lineHourLevelQtysTempMap.get(currLineId).get(rec.hour).totalOperators.add(rec.operator_id);
      lineHourLevelQtysTempMap.get(currLineId).get(rec.hour).totalWorkstations.add(rec.workstation_id);
      lineHourLevelQtysTempMap.get(currLineId).get(rec.hour).totalStyles.add(rec.style_id);
      if(rec.is_rework) {
        lineHourLevelQtysTempMap.get(currLineId).get(rec.hour).totalRework += rec.good_quantity;
      }
      if(rec.is_last_op){
        lineHourLevelQtysTempMap.get(currLineId).get(rec.hour).totalOutput += rec.good_quantity;
      }
      if(rec.good_quantity > 0) { 
        lineHourLevelQtysTempMap.get(currLineId).get(rec.hour).accountableWorkstations.add(rec.workstation_id);
      }
      lineHourLevelQtysTempMap.get(currLineId).get(rec.hour).productiveMinutes += Math.round(rec.operation_smv * rec.good_quantity);
      lineWorkstationsMap.get(rec.line_id).add(rec.workstation_id);
    });

    // get the downtimes at the line level
    const downtimeQuery = this.downTimeEntity.createQueryBuilder('dt')
    .select(` SUM(downtime) as downtime_secs, line_id `)
    .where(` plan_date = '${date}' AND unit_id = '${unitId}' `)
    .groupBy(` unit_id, plan_date, line_id `);
    const downtimeResult: {downtime_secs: number, line_id: string}[] = await downtimeQuery.getRawMany();
    downtimeResult.forEach(r => {
      lineDowntimeMap.set(r.line_id, Math.round(r.downtime_secs/60));
    })


    // get the line efficiencies
    const effQuery = this.lineEffEnt.createQueryBuilder('eff')
    .select(' line_id, SUM(efficiency) as eff, count(1) as items, hour')
    .where(` plan_date = '${date}' AND unit_id = '${unitId}' `)
    .groupBy(` unit_id, plan_date, line_id, hour`);
    const effResult: {line_id: string, eff: number, items: number, hour: number}[] = await effQuery.getRawMany();
    effResult.forEach(rec => {
      // set the line eff
      if(linePerHourAbstarctInfo?.get(rec.line_id)?.get(rec.hour)) {
        const lineRef = linePerHourAbstarctInfo.get(rec.line_id).get(rec.hour);
        lineRef.efficiency = Math.round(rec.eff/rec.items);
      }
    })

    // set the final outcomes to each line
    lineHourLevelQtysTempMap.forEach((lineHourInfo, lineId) => {
      lineHourInfo.forEach((lineInfo, hour) => {
        const lineRef = linePerHourAbstarctInfo.get(lineId)?.get(hour);
        if(lineRef) {
          lineRef.noOfOperators = lineInfo.totalOperators.size;
          lineRef.outputQuantity = lineInfo.totalOutput;
          lineRef.plannedQuantity = linePerHourPlan.get(lineId).get(hour);
          lineRef.reworkQuantity = lineInfo.totalRework;
          lineRef.totalWorkstations = lineInfo.totalWorkstations.size;
          lineRef.totalStyles = lineInfo.totalStyles.size;
          lineRef.productiveMinutes = Math.round(lineInfo.productiveMinutes/60);
          lineRef.idleWorkstations = lineInfo.totalWorkstations.size - lineInfo.accountableWorkstations.size;
        }
      });
    });

    // console.log(linePerHourAbstarctInfo);
    const lineAbstracts: LineAbstractInfoModel[] = [];
    linePerHourAbstarctInfo.forEach((lineHourInfo, lineId) => {
      const line = lineInfoMap.get(lineId);
      const lineAbstract = new LineAbstractInfoModel(lineId, line.lineNumber, 0, 0, 0, 0, 0, 0, 0, 0, []);
      let totalObservations = 0;
      lineHourInfo.forEach(r => {
        lineAbstract.planQuantity += r.plannedQuantity;
        lineAbstract.outputQuantity += r.outputQuantity;
        lineAbstract.reworkQuantity += r.reworkQuantity;
        lineAbstract.productiveMinutes += Math.round(r.productiveMinutes);
        lineAbstract.hourWiseLinePerfAbstractInfo.push(r);
        lineAbstract.totalWorkstations = lineWorkstationsMap.get(lineId)?.size;
        lineAbstract.efficiency += r.efficiency;
        if(r.productiveMinutes > 0)
          totalObservations++;
      });
      lineAbstract.efficiency = totalObservations > 0 ? Math.round(lineAbstract.efficiency/totalObservations) : 0;
      lineAbstract.downtimeMinutes = lineDowntimeMap.get(lineId);
      lineAbstracts.push(lineAbstract);
    })
    return lineAbstracts;
  }



}


